import jsPDF from "jspdf";
import {isCertificateApprovalComplete} from "@/utils/certificateApproval";
import moment from "moment";
import certificateImage from "../../assets/img/certificate_template.jpg";
import cat2CertificateImage from "../../assets/img/certificate_template_cat2.jpg";
import {TrainerService} from "../Trainers/trainers.service";
import {TraineeService} from "./trainees.service";

const resolveImageType = (value) => {
    if (!value || typeof value !== "string") {
        return "";
    }

    const normalized = value.split("?")[0].toLowerCase();
    if (normalized.startsWith("data:image/png")) {
        return "PNG";
    }
    if (
        normalized.startsWith("data:image/jpeg") ||
        normalized.startsWith("data:image/jpg")
    ) {
        return "JPEG";
    }

    const extension = normalized.includes(".")
        ? normalized.substring(normalized.lastIndexOf(".") + 1)
        : normalized;

    switch (extension) {
        case "jpg":
        case "jpeg":
            return "JPEG";
        case "png":
            return "PNG";
        default:
            return "";
    }
};

const safeJoin = (...values) =>
    values
        .map((value) => (value == null ? "" : `${value}`.trim()))
        .filter(Boolean)
        .join(" ");

const formatDate = (value) => {
    if (!value) {
        return "-";
    }

    const formatted = moment(value);
    return formatted.isValid() ? formatted.format("MMM DD, YYYY") : "-";
};

const normalizeEmailValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    const text = String(value).trim();
    if (!text) {
        return "";
    }

    const lowered = text.toLowerCase();
    if (lowered === "undefined" || lowered === "null") {
        return "";
    }

    return text;
};

const extractEmailFromContacts = (contacts) => {
    if (!Array.isArray(contacts)) {
        return null;
    }

    for (const contact of contacts) {
        const email = normalizeEmailValue(
            typeof contact === "string" ? contact : contact?.email
        );
        if (email) {
            return email;
        }
    }

    return null;
};

export const resolveCertificateEmail = (userInfo) => {
    const directCandidates = [
        userInfo?.facilityEmail,
        userInfo?.emailAddress,
        userInfo?.email,
        userInfo?.trainee?.facilityEmail,
        userInfo?.trainee?.emailAddress,
        userInfo?.trainee?.email,
        userInfo?.person?.emailAddress,
        userInfo?.person?.email,
    ];

    for (const candidate of directCandidates) {
        const normalized = normalizeEmailValue(candidate);
        if (normalized) {
            return normalized;
        }
    }

    return (
        extractEmailFromContacts(userInfo?.contacts) ||
        extractEmailFromContacts(userInfo?.person?.contacts) ||
        extractEmailFromContacts(userInfo?.trainee?.contacts) ||
        null
    );
};

export const generateCertificate1 = async (
    traineeUpdateCourse,
    userInfo,
    imageSrc,
    options = {}
) => {
    const {
        open = true,
        sendEmail = true,
        returnBlob = false,
        allowPending = false,
    } = options || {};
    if (!allowPending && !isCertificateApprovalComplete(traineeUpdateCourse)) {
        throw new Error("Certificate must be approved before it can be generated.");
    }
    const doc = new jsPDF({
        orientation: "landscape",
        filters: ["ASCIIHexEncode"],
    });
    const SIGNATURE_BOX_WIDTH = 44;
    const SIGNATURE_BOX_HEIGHT = 20;
    const SIGNATURE_SCALE_FACTOR = 1.45;
    const SIGNATURE_MIN_WIDTH = 28;
    const SIGNATURE_MIN_HEIGHT = 11;
    const SIGNATURE_MAX_WIDTH = 44;
    const SIGNATURE_MAX_HEIGHT = 24;
    const getSignatureSize = (imageData) => {
        try {
            const signProps = doc.getImageProperties(imageData);
            const sourceWidth = Number(signProps?.width) || SIGNATURE_BOX_WIDTH;
            const sourceHeight = Number(signProps?.height) || SIGNATURE_BOX_HEIGHT;
            const fitScale = Math.min(
                SIGNATURE_BOX_WIDTH / sourceWidth,
                SIGNATURE_BOX_HEIGHT / sourceHeight
            );

            let width = sourceWidth * fitScale * SIGNATURE_SCALE_FACTOR;
            let height = sourceHeight * fitScale * SIGNATURE_SCALE_FACTOR;
            const growScale = Math.max(
                SIGNATURE_MIN_WIDTH / width,
                SIGNATURE_MIN_HEIGHT / height,
                1
            );
            width *= growScale;
            height *= growScale;
            const shrinkScale = Math.min(
                SIGNATURE_MAX_WIDTH / width,
                SIGNATURE_MAX_HEIGHT / height,
                1
            );

            return {width: width * shrinkScale, height: height * shrinkScale};
        } catch (error) {
            return {width: 32, height: 12};
        }
    };
    const addSignature = (imageData, imageType, x, y) => {
        if (!imageData || !imageType) {
            return;
        }
        const {width, height} = getSignatureSize(imageData);
        const drawX = x + (SIGNATURE_BOX_WIDTH - width) / 2;
        const drawY = y + (SIGNATURE_BOX_HEIGHT - height) / 2;
        doc.addImage(imageData, imageType, drawX, drawY, width, height);
    };
    const addProviderImage = async (url, x, y, width, height) => {
        const imageType = resolveImageType(url);
        if (!url || !imageType) {
            return;
        }

        try {
            const response = await TrainerService.getImage({url});
            const imageData = response?.data?.data || "";
            if (!imageData) {
                return;
            }
            doc.addImage(imageData, imageType, x, y, width, height);
        } catch (error) {
            // Certificate generation should continue even if an optional provider asset fails.
        }
    };
    const providerName =
        traineeUpdateCourse?.program?.cpdProvider?.trainingCenterName ||
        traineeUpdateCourse?.course?.courseOwner?.trainingCenterName ||
        "-";
    const certificateTitle =
        traineeUpdateCourse?.course?.courseName ||
        traineeUpdateCourse?.program?.courseTitleCat2 ||
        "-";
    const traineeName = userInfo?.trainee
        ? safeJoin(
            userInfo?.trainee?.firstName,
            userInfo?.trainee?.middleName,
            userInfo?.trainee?.lastName
        )
        : safeJoin(
            userInfo?.person?.firstname,
            userInfo?.person?.middlename,
            userInfo?.person?.lastname
        );
    const qrImageType = resolveImageType(imageSrc) || "JPEG";
    let logoUrl = traineeUpdateCourse?.program?.cpdProvider?.logo_url || "";
    let stampUrl = traineeUpdateCourse?.program?.cpdProvider?.stamp_url || "";
    let signature1Url = traineeUpdateCourse?.program?.cpdProvider?.signature1_url || "";
    let signature2Url = traineeUpdateCourse?.program?.cpdProvider?.signature2_url || "";
    // let signature3Url = traineeUpdateCourse?.program?.cpdProvider?.signature3_url || "";

    const sign1Extension = resolveImageType(signature1Url);
    const sign2Extension = resolveImageType(signature2Url);
    // const lastSign3DotIndex = signature3Url ? signature3Url.lastIndexOf(".") : -1;
    // let sign3Extension = "";
    // if (lastSign3DotIndex !== -1) {
    //   sign3Extension = signature3Url.substring(lastSign3DotIndex + 1).toLowerCase();
    //
    //   switch (sign3Extension) {
    //     case "jpg":
    //     case "jpeg":
    //       sign3Extension = "JPEG";
    //       break;
    //     case "png":
    //       sign3Extension = "PNG";
    //       break;
    //     default:
    //       sign3Extension = "";
    //       break;
    //   }
    // }
    if (traineeUpdateCourse?.program?.category?.categoryType === "CAT1")
        doc.addImage(certificateImage, "JPEG", 0, 0, 298, 213, undefined, "FAST");
    else
        doc.addImage(cat2CertificateImage, "JPEG", 0, 0, 298, 213, undefined, "FAST");

    await addProviderImage(logoUrl, 138, 30, 25, 25);
    await addProviderImage(stampUrl, 120, 155, 30, 30);
    if (signature1Url != "") {
        try {
            const sign1Resp = await TrainerService.getImage({url: signature1Url});
            const sign1Image = sign1Resp?.data?.data || "";
            addSignature(sign1Image, sign1Extension, 18, 154);
        } catch (error) {
            // Signature is optional for certificate rendering.
        }
    }
    if (signature2Url != "") {
        try {
            const sign2Resp = await TrainerService.getImage({url: signature2Url});
            const sign2Image = sign2Resp?.data?.data || "";
            addSignature(sign2Image, sign2Extension, 208, 154);
        } catch (error) {
            // Signature is optional for certificate rendering.
        }
    }
    // if (signature3Url != "") {
    //   const sign3Resp = await TrainerService.getImage({ url: signature3Url });
    //   const sign3Image =
    //     sign3Resp && sign3Resp.data && sign3Resp.data.data ? sign3Resp.data.data : "";
    //
    //   doc.addImage(sign3Image, sign3Extension, 220, 155, 20, 20);
    // }

    doc.setTextColor("#B68646");
    let pageWidth = doc.internal.pageSize.getWidth();
    doc.setFont;
    doc.text(
        pageWidth / 2 - doc.getTextWidth(providerName) / 2 + 13,
        68,
        providerName.toUpperCase()
    );
    const certificateReason =
        traineeUpdateCourse?.certificate_reason?.name ||
        traineeUpdateCourse?.program?.certificate_reason?.name ||
        "-";

    doc.setTextColor("#B68646");

    doc.setTextColor("black");
    doc.setFontSize(15);
    doc.setTextColor("black");
    doc.setFontSize(15);

    doc.text(
        175,
        89,
        `${
            userInfo.trainee && userInfo.trainee.prefix
                ? userInfo.trainee.prefix
                : userInfo.person && userInfo.person.prefix
                    ? userInfo.person.prefix
                    : "-"
        }`
    );
    userInfo.trainee
        ? doc.text(
            120,
            102,
            traineeName || "-"
        )
        : doc.text(
            120,
            102,
            traineeName || "-"
        );
    doc.setFontSize(12);
    doc.text(120, 114, `Has ${certificateReason}`);
    doc.setFontSize(15);

    doc.text(
        pageWidth / 2 - doc.getTextWidth(certificateTitle) / 2,
        122,
        certificateTitle
    );
    doc.setFontSize(12);
    doc.text(
        65,
        135,
        `${traineeUpdateCourse?.course?.courseCode ?? ''}`
    );
    doc.text(
        65,
        143,
        `${
            traineeUpdateCourse.course
                ? (traineeUpdateCourse.program?.ceu ?? traineeUpdateCourse.course.continualEducationUnit)
                : traineeUpdateCourse.program
                    ? traineeUpdateCourse.program.ceu
                    : "-"
        }`
    );
    doc.text(
        80,
        150.3,
        formatDate(traineeUpdateCourse.courseCompletionDate)
    );

    doc.text(
        208,
        135.5,
        `${traineeUpdateCourse?.course?.courseAccreditor?.accreditorName ?? ''}`
    );

    doc.text(
        220,
        143,
        `${
            traineeUpdateCourse?.program?.courseLevel?.name ?? traineeUpdateCourse?.course?.courseLevel?.name ?? ''}`
    );

    doc.text(
        185,
        150.5,
        formatDate(traineeUpdateCourse?.program?.startDate)
    );

    doc.text(
        235,
        150.5,
        formatDate(traineeUpdateCourse?.program?.endDate)
    );

    doc.setFontSize(15);
    doc.text(
        40,
        193,
        `${traineeUpdateCourse?.program?.collaborator ?
            'Collaborator\'s Names: ' : ""}`
    );

    doc.setFontSize(12);
    doc.text(
        93,
        193,
        `${traineeUpdateCourse?.program?.collaborator ?? ""}`
    );
    if (imageSrc) {
        doc.addImage(imageSrc, qrImageType, 235, 14, 35, 35);
    }

    if (open) {
        window.open(doc.output("bloburl"));
    }

    let pdfBlob = null;
    if (sendEmail || returnBlob) {
        pdfBlob = doc.output("blob");
    }

    if (sendEmail) {
        try {
            const formData = new FormData();
            formData.append('file', pdfBlob, 'certificate.pdf');
            const name = userInfo?.trainee
                ? (`${userInfo?.trainee?.firstName} ${userInfo?.trainee?.middleName} ${userInfo?.trainee?.lastName}`)
                : (userInfo?.person
                    ? (`${userInfo?.person?.firstname} ${userInfo?.person?.middlename} ${userInfo?.person?.lastname}`)
                    : '');
            const email = resolveCertificateEmail(userInfo);
            const course = traineeUpdateCourse?.course?.courseName ?? traineeUpdateCourse?.program?.courseTitleCat2;
            const cpdProvider =
                traineeUpdateCourse?.program?.cpdProvider ??
                traineeUpdateCourse?.course?.courseOwner;
            const cpd = cpdProvider?.trainingCenterName ?? "";
            const cpdProviderPhone = cpdProvider?.institutionPhoneNumber;
            const cpdProviderEmail = cpdProvider?.institutionEmailAddress;
            formData.append('name', name);
            formData.append('email', email);
            formData.append('course', course);
            formData.append('cpd', cpd);
            formData.append('cpdProviderPhone', cpdProviderPhone ?? "");
            formData.append('cpdProviderEmail', cpdProviderEmail ?? "");
            await TraineeService.sendEmail(formData);
        } catch (e) {
            // PDF generation already succeeded; email delivery failure should not block it.
        }
    }

    if (returnBlob) {
        return pdfBlob;
    }
};
